import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { convertImage, getTaskStatus } from '../../services/chatApi';
import { Download, Upload, FileType, RefreshCw, Loader2, FileQuestion } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer'; 

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};


const ConvertImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null); // This is the base64 string
    const [processedFormat, setProcessedFormat] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const supportedFormats = [
        "JPEG", "PNG", "GIF", "BMP", "TIFF", "ICO", "ICNS", "PCX", "PPM", "SGI", "TGA", "WEBP"
    ];
    
    // Formats that browsers can actually display in an <img> tag
    const webSafeFormats = ["JPEG", "PNG", "GIF", "BMP", "WEBP", "ICO"];

    const [targetFormat, setTargetFormat] = useState("PNG");
    const pollInterval = useRef(null);
    // Handle incoming navigation
    const resetParams = useCallback(() => {
        setTargetFormat("PNG");
        setProcessedFormat(null);
        setError(null);
        setIsLoading(false);
    }, []);

    // 2. Activate Image Transfer Hook
    // This replaces the manual useEffect for location.state
    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            setError(null);
            resetParams();
        }
    };

    const handleProcess = async () => {
        if (!file) return;
        setIsLoading(true);
        setError(null);
        setProcessedImage(null);

        try {
            // 1. Start the Task
            const startResponse = await convertImage(file, targetFormat);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            // 2. Poll for results
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state; // Celery sometimes uses 'state' or 'status'

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        // Backend result: { download_url: "https://...", file_id: "..." }
                        const result = statusRes.result || statusRes.info; 
                        if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }
                        if (result.download_url) {
                            setProcessedImage(result.download_url); // Store the S3 URL
                            setProcessedFormat(targetFormat);
                            setIsLoading(false);
                        } else {
                            setError("Invalid response format.");
                            setIsLoading(false);
                        }
                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('convertImage.errors.generic'));
                        console.error("Task Failed:", statusRes.result);
                    }
                    // If 'PENDING' or 'STARTED', do nothing, just wait for next poll
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error while checking status.");
                }
            }, 2000); // Check every 2 seconds

        } catch (err) {
            setIsLoading(false);
            console.error(err);
            setError(t('convertImage.errors.generic'));
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        
        try {
            // Since it is an S3 URL, we fetch it as a blob to force download
            // instead of opening in a new tab
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = `converted_image.${processedFormat.toLowerCase()}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            // Fallback for simple link click
            window.open(processedImage, '_blank');
        }
    };


    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        setTargetFormat("PNG");
        setProcessedFormat(null);
        setError(null);
        clearInterval(pollInterval.current);
    };


    const isResultWebSafe = processedFormat && webSafeFormats.includes(processedFormat.toUpperCase());

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('convertImage.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-2xl">
                    {t('convertImage.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="convert-upload" />
                        <label htmlFor="convert-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('convertImage.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('convertImage.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left Column: Images */}
                        <div className="lg:col-span-8 order-2 lg:order-1">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Original */}
                                <div>
                                    <p className="text-sm font-semibold text-slate-500 mb-2">{t('convertImage.labels.original')}</p>
                                    <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                                        <img src={previewUrl} alt="Original" className="w-full h-auto" />
                                    </div>
                                </div>

                                {/* Result */}
                                <div>
                                    <p className="text-sm font-semibold text-brand-orange mb-2">{t('convertImage.labels.result')}</p>
                                    <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200 min-h-[200px] flex items-center justify-center relative">
                                        {isLoading ? (
                                            <div className="flex flex-col items-center gap-2 text-brand-orange">
                                                <Loader2 className="animate-spin" size={32} />
                                                <span className="text-sm">{t('convertImage.status.converting')}</span>
                                            </div>
                                        ) : processedImage ? (
                                            isResultWebSafe ? (
                                                <img src={processedImage} alt="Converted" className="w-full h-auto" />
                                            ) : (
                                                <div className="text-center p-6 text-slate-500">
                                                    <FileQuestion size={48} className="mx-auto mb-2 opacity-50" />
                                                    <p className="text-sm font-medium">{processedFormat}</p>
                                                    <p className="text-xs mt-1">{t('convertImage.status.noPreview')}</p>
                                                </div>
                                            )
                                        ) : (
                                            <div className="opacity-50 grayscale">
                                                <img src={previewUrl} alt="Preview" className="w-full h-auto" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Tool Navigator - Only show if result is web safe, otherwise next tool breaks */}
                            {processedImage && isResultWebSafe && (
                                <ToolNavigator currentImage={processedImage} />
                            )}
                        </div>

                        {/* Right Column: Controls */}
                        <div className="lg:col-span-4 order-1 lg:order-2 space-y-6">
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                <h4 className="font-semibold mb-4 flex items-center gap-2 text-brand-orange">
                                    <FileType size={20} /> {t('convertImage.controls.title')}
                                </h4>
                                
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        {t('convertImage.controls.format')}
                                    </label>
                                    <select 
                                        value={targetFormat}
                                        onChange={(e) => setTargetFormat(e.target.value)}
                                        className="w-full p-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-brand-orange focus:outline-none bg-white"
                                    >
                                        {supportedFormats.map(fmt => (
                                            <option key={fmt} value={fmt}>{fmt}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="pt-4 border-t border-slate-100">
                                    {!processedImage ? (
                                        <button 
                                            onClick={handleProcess} 
                                            disabled={isLoading} 
                                            className="w-full bg-brand-orange text-white py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-70"
                                        >
                                            {isLoading ? <Loader2 className="animate-spin" /> : <RefreshCw />} 
                                            {t('convertImage.buttons.convert')}
                                        </button>
                                    ) : (
                                        <>
                                            <button onClick={handleDownload} className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2 font-semibold mb-3">
                                                <Download size={18} /> {t('convertImage.buttons.download', { format: processedFormat })}
                                            </button>
                                            <button onClick={resetTool} className="w-full bg-slate-100 text-slate-700 py-3 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2">
                                                <RefreshCw size={18} /> {t('convertImage.buttons.newImage')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                            {error && <div className="text-center p-3 bg-red-50 text-red-600 rounded-md border border-red-200 text-sm">{error}</div>}
                        </div>

                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default ConvertImage;
